import { Box, Button, Divider, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPagination, GridPaginationModel, GridValueGetterParams } from "@mui/x-data-grid";
import React, { ChangeEvent, FC, FormEvent, useEffect, useMemo, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import "./reporting.css";
import { PAGINATION_CONSTANTS } from "../../../utils/constants";
import AddVehicleAlertDialog from "./AddVehicleAlertDialog";
import {
	useEditSocietyConfigMutation,
	useGetSocietyConfigQuery,
	useGetVehiclesListQuery,
	useUpdateVehicleMutation
} from "../../../redux/reducers/settings.reducer";
import { isEqual, maxBy } from "lodash";
import { LoadingButton } from "@mui/lab";
import { useAppDispatch } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/feedbackNotification.reducer";
import { IGetVehiclesListRequestData } from "../../../types";
import TablePagination from "../../../components/TablePagination";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { GridRenderCellParams } from "@mui/x-data-grid";
import AddToMailingListDialog, { IMailingInputDetails } from "./AddToMailingListDialog";
import ReportHistoryDialog from "./ReportHistoryDialog";

export interface IMailingListDetails {
	sr_no: number;
	first_name: string;
	last_name: string;
	email_address: string;
	contact_number: string;
	designation: string;
}

interface IVehicleAlertRowDetails {
	id: number;
	sr_no: number;
	vehicle_number: string;
}

const VehiclesPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const Reporting: FC = () => {
	const dispatch = useAppDispatch();

	const [showAddToMailingListDialog, setShowAddToMailingListDialog] = useState<boolean>(false);
	const [vehicleAlertSearchInput, setVehicleAlertSearchInput] = useState<string>("");
	const [showAddVehicleAlertDialog, setShowAddVehicleAlertDialog] = useState<boolean>(false);
	const [selectedVehicleDetails, setSelectedVehicleDetails] = useState<IVehicleAlertRowDetails | null>(null);

	const [filters, setFilters] = useState<IGetVehiclesListRequestData>({
		page: 1,
		send_alert: true
	});

	const [mailingListRowsData, setMailingListRowsData] = useState<IMailingListDetails[]>([]);
	const [addToMailingListError, setAddToMailingListError] = useState<string>("");
	const [selectedMailingDetails, setSelectedMailingDetails] = useState<IMailingListDetails | null>(null);
	const [showReportHistoryDialog, setShowReportHistoryDialog] = useState<boolean>(false);

	// APIS
	// GET SOCIETY CONFIG
	const {
		data: getSocietyConfigResponse,
		isLoading: getSocietyConfigLoading,
		isFetching: getSocietyConfigFetching,
		refetch: refetchSocietyConfig
	} = useGetSocietyConfigQuery(null);

	// EDIT SOCIETY CONFIG - MAILING LIST
	const [
		editMailingList,
		{
			isLoading: editMailingListLoading,
			error: editMailingListError,
			isSuccess: editMailingListSuccess,
			reset: resetEditMailingList
		}
	] = useEditSocietyConfigMutation();

	// GET VEHICLES LIST
	const {
		data: getVehiclesListResponse,
		isLoading: getVehiclesListLoading,
		isFetching: getVehiclesListFetching,
		refetch: refetchVehiclesList
	} = useGetVehiclesListQuery(filters);

	// UPDATE VEHICLE
	const [
		updateVehicle,
		{
			isLoading: updateVehicleLoading,
			error: updateVehicleError,
			isSuccess: updateVehicleSuccess,
			reset: resetUpdateVehicleState
		}
	] = useUpdateVehicleMutation();

	const societyConfig = useMemo(
		() =>
			getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
				? getSocietyConfigResponse.results[0]
				: null,
		[getSocietyConfigResponse]
	);

	const vehicleAlertRowsData = useMemo<IVehicleAlertRowDetails[]>(() => {
		if (getVehiclesListResponse) {
			return getVehiclesListResponse.results.map((item, index) => ({
				id: item.id,
				sr_no: (filters.page - 1) * PAGINATION_CONSTANTS.ROWS_PER_PAGE + index + 1,
				vehicle_number: item.number_plate
			}));
		}

		return [];
	}, [filters.page, getVehiclesListResponse]);

	const initialMailingListRowsData = useMemo<IMailingListDetails[]>(() => {
		if (societyConfig && societyConfig.mailing_list) {
			return societyConfig.mailing_list.map((item, index) => ({
				sr_no: index + 1,
				first_name: item.first_name,
				last_name: item.last_name,
				contact_number: item.contact_number,
				email_address: item.email_address,
				designation: item.designation
			}));
		}

		return [];
	}, [societyConfig]);

	function handleOpenAddToMailingListDialog() {
		setShowAddToMailingListDialog(true);
	}

	function handleCloseAddToMailingListDialog() {
		setShowAddToMailingListDialog(false);
		setAddToMailingListError("");
	}

	function handleOpenAddVehicleAlertDialog() {
		setShowAddVehicleAlertDialog(true);
	}

	function handleCloseAddVehicleAlertDialog() {
		setShowAddVehicleAlertDialog(false);
		refetchVehiclesList();
	}

	function handleChangeVehicleAlertSearchInput(event: ChangeEvent<HTMLInputElement>) {
		setVehicleAlertSearchInput(event.target.value);
	}

	function handleVehicleAlertSearchFormSubmit(event: FormEvent) {
		event.preventDefault();
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters };

			if (vehicleAlertSearchInput) updatedFilters.search = vehicleAlertSearchInput;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handleClearVehicleAlertSearchInput() {
		setVehicleAlertSearchInput("");
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters };
			delete updatedFilters.search;
			return updatedFilters;
		});
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
	}

	function handleOpenDeleteVehicleConfirmationDialog(vehicleDetails: IVehicleAlertRowDetails) {
		setSelectedVehicleDetails(vehicleDetails);
	}

	function handleCloseDeleteVehicleConfirmationDialog() {
		setSelectedVehicleDetails(null);
	}

	function handleConfirmDeleteVehicle() {
		if (selectedVehicleDetails) {
			updateVehicle({
				id: selectedVehicleDetails.id,
				data: {
					number_plate: selectedVehicleDetails.vehicle_number,
					send_alert: false
				}
			});
		}
	}

	function handleSubmitAddToMailingList(mailingDetails: IMailingInputDetails) {
		const updatedList = [...mailingListRowsData];

		const existingEmail = updatedList.find((item) => item.email_address === mailingDetails.email_address);

		if (existingEmail) {
			setAddToMailingListError("Email already exists");
		} else {
			updatedList.push({
				sr_no: (maxBy(updatedList, "sr_no")?.sr_no ?? 0) + 1,
				first_name: mailingDetails.first_name,
				last_name: mailingDetails.last_name,
				contact_number: mailingDetails.contact_number,
				email_address: mailingDetails.email_address,
				designation: mailingDetails.designation
			});

			setAddToMailingListError("");
			setMailingListRowsData(updatedList);
			handleCloseAddToMailingListDialog();
		}
	}

	function handleMailingListSave() {
		if (societyConfig) {
			editMailingList({
				id: societyConfig.id,
				data: {
					mailing_list: mailingListRowsData.map((item) => ({
						first_name: item.first_name,
						last_name: item.last_name,
						contact_number: item.contact_number,
						email_address: item.email_address,
						designation: item.designation,
						is_subscribed: true
					}))
				}
			});
		}
	}

	function handleOpenDeleteConfirmationDialog(mailingDetails: IMailingListDetails) {
		setSelectedMailingDetails(mailingDetails);
	}

	function handleCloseDeleteConfirmationDialog() {
		setSelectedMailingDetails(null);
	}

	function handleDeleteMailingDetails() {
		if (selectedMailingDetails) {
			setMailingListRowsData((currentList) => {
				const updatedList = [...currentList];

				const index = updatedList.findIndex((item) => item.email_address === selectedMailingDetails.email_address);
				if (index >= 0) updatedList.splice(index, 1);

				return updatedList;
			});
		}

		handleCloseDeleteConfirmationDialog();
	}

	function handleOpenReportHistoryDialog() {
		setShowReportHistoryDialog(true);
	}

	function handleCloseReportHistoryDialog() {
		setShowReportHistoryDialog(false);
	}

	useEffect(() => {
		if (updateVehicleError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in updateVehicleError && updateVehicleError.data
							? String(updateVehicleError.data)
							: "Failed to remove vehicle number alert",
					severity: "error"
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateVehicleError]);

	useEffect(() => {
		if (updateVehicleSuccess) {
			handleCloseDeleteVehicleConfirmationDialog();
			resetUpdateVehicleState();
			refetchVehiclesList();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateVehicleSuccess]);

	useEffect(() => {
		if (societyConfig && societyConfig.mailing_list) {
			setMailingListRowsData(
				societyConfig.mailing_list.map((item, index) => ({
					sr_no: index + 1,
					first_name: item.first_name,
					last_name: item.last_name,
					contact_number: item.contact_number,
					email_address: item.email_address,
					designation: item.designation
				}))
			);
		}
	}, [societyConfig]);

	useEffect(() => {
		if (editMailingListError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in editMailingListError && editMailingListError.data
							? String(editMailingListError.data)
							: "Failed to save mailing list",
					severity: "error"
				})
			);

			resetEditMailingList();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editMailingListError]);

	useEffect(() => {
		if (editMailingListSuccess) {
			dispatch(
				showFeedbackNotification({
					message: "Mailing list saved successfully",
					severity: "success"
				})
			);

			resetEditMailingList();
			refetchSocietyConfig();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editMailingListSuccess]);

	const mailingListColumnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 100,
			sortable: false
		},
		{
			field: "name",
			headerName: "Name",
			minWidth: 200,
			flex: 1,
			sortable: false,
			valueGetter: (params: GridValueGetterParams<IMailingListDetails>) =>
				`${params.row.first_name} ${params.row.last_name}`.trim()
		},
		{
			field: "contact_number",
			headerName: "Contact Number",
			minWidth: 150,
			flex: 1,
			sortable: false
		},
		{
			field: "email_address",
			headerName: "Email Address",
			minWidth: 220,
			flex: 1.5,
			sortable: false
		},
		{
			field: "designation",
			headerName: "Designation",
			minWidth: 150,
			flex: 1,
			sortable: false
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IMailingListDetails>) => (
				<Box className="communication-channels-table-actions-column-cell">
					<IconButton size="small" color="error" onClick={() => handleOpenDeleteConfirmationDialog(params.row)}>
						<DeleteIcon fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	const vehicleAlertColumnsData: GridColDef[] = [
		{ field: "sr_no", headerName: "Sr. No.", width: 100, sortable: false },
		{ field: "vehicle_number", headerName: "Vehicle Number", minWidth: 200, flex: 1, sortable: false },
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IVehicleAlertRowDetails>) => (
				<Box className="communication-channels-table-actions-column-cell">
					<IconButton size="small" color="error" onClick={() => handleOpenDeleteVehicleConfirmationDialog(params.row)}>
						<DeleteIcon fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="communication-channels-screen-wrapper">
			<Box className="communication-channels-screen-actions-wrapper">
				<Typography variant="h6" color="var(--color-primary-main)">
					Mailing List
				</Typography>

				<Button
					disableElevation
					variant="contained"
					color="primary"
					startIcon={<HistoryIcon />}
					onClick={handleOpenReportHistoryDialog}
				>
					History
				</Button>
			</Box>

			<Box className="communication-channels-screen-header-wrapper">
				<Typography variant="body2" color="#00000080">
					An email update will be sent at 9:00 AM daily.
				</Typography>

				<Divider sx={{ marginY: "0.5rem", borderColor: "var(--color-text-primary)" }} />
			</Box>

			<Box className="communication-channels-screen-actions-wrapper">
				<Button
					variant="outlined"
					color="success"
					startIcon={<AddCircleIcon />}
					sx={{ paddingX: 2 }}
					onClick={handleOpenAddToMailingListDialog}
				>
					Add
				</Button>
			</Box>

			<Box className="communication-channels-table-wrapper">
				<DataGrid
					columns={mailingListColumnsData}
					rows={mailingListRowsData}
					disableRowSelectionOnClick
					disableColumnMenu
					rowSpacingType="border"
					density="compact"
					columnHeaderHeight={60}
					pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
					slots={{ pagination: VehiclesPagination }}
					slotProps={{ footer: { sx: { justifyContent: "center" } } }}
					hideFooter={mailingListRowsData.length <= PAGINATION_CONSTANTS.ROWS_PER_PAGE}
					loading={getSocietyConfigLoading || getSocietyConfigFetching}
					getRowId={(row) => row.email_address}
					classes={{
						columnSeparator: "communication-channels-table-column-separator",
						columnHeader: "communication-channels-table-column-header"
					}}
					sx={{ minHeight: 78 }}
				/>
			</Box>

			<Box>
				<LoadingButton
					disableElevation
					variant="contained"
					color="success"
					startIcon={<SaveIcon />}
					disabled={isEqual(initialMailingListRowsData, mailingListRowsData)}
					onClick={handleMailingListSave}
					loading={editMailingListLoading}
					loadingPosition="start"
				>
					Save Changes
				</LoadingButton>
			</Box>

			<Typography variant="h6" color="var(--color-primary-main)">
				Vehicle Number Alerts
			</Typography>

			<Box className="communication-channels-screen-actions-wrapper">
				<Button
					variant="outlined"
					color="success"
					startIcon={<AddCircleIcon />}
					sx={{ paddingX: 2 }}
					onClick={handleOpenAddVehicleAlertDialog}
				>
					Add
				</Button>

				<Box component="form" noValidate onSubmit={handleVehicleAlertSearchFormSubmit}>
					<TextField
						size="small"
						variant="filled"
						placeholder="Search"
						value={vehicleAlertSearchInput}
						onChange={handleChangeVehicleAlertSearchInput}
						InputProps={{
							hiddenLabel: true,
							disableUnderline: true,
							classes: { root: "filled-input-root" },
							endAdornment: (
								<InputAdornment position="end">
									{filters.search ? (
										<IconButton edge="end" size="small" onClick={handleClearVehicleAlertSearchInput}>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									) : null}

									<IconButton edge="end" color="primary" size="small" type="submit">
										<SearchIcon />
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
				</Box>
			</Box>

			<Box className="communication-channels-table-wrapper">
				<DataGrid
					columns={vehicleAlertColumnsData}
					rows={vehicleAlertRowsData}
					disableRowSelectionOnClick
					disableColumnMenu
					rowSpacingType="border"
					density="compact"
					columnHeaderHeight={60}
					pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
					onPaginationModelChange={handlePageChange}
					rowCount={getVehiclesListResponse?.count ?? 0}
					paginationModel={{ page: filters.page - 1, pageSize: PAGINATION_CONSTANTS.ROWS_PER_PAGE }}
					slots={{ pagination: VehiclesPagination }}
					slotProps={{ footer: { sx: { justifyContent: "center" } } }}
					hideFooter={(getVehiclesListResponse?.count ?? 0) <= PAGINATION_CONSTANTS.ROWS_PER_PAGE}
					loading={getVehiclesListLoading || getVehiclesListFetching}
					classes={{
						columnSeparator: "communication-channels-table-column-separator",
						columnHeader: "communication-channels-table-column-header"
					}}
					sx={{ minHeight: 78 }}
				/>
			</Box>

			<AddVehicleAlertDialog open={showAddVehicleAlertDialog} onClose={handleCloseAddVehicleAlertDialog} />

			<ConfirmationDialog
				open={!!selectedVehicleDetails}
				title="Remove Vehicle Number Alert"
				heading="Are you sure you want to remove alert for the vehicle number?"
				onClose={handleCloseDeleteVehicleConfirmationDialog}
				onConfirm={handleConfirmDeleteVehicle}
				confirmButtonLoading={updateVehicleLoading}
				width="sm"
				color="error"
			/>

			<AddToMailingListDialog
				open={showAddToMailingListDialog}
				error={addToMailingListError}
				onClose={handleCloseAddToMailingListDialog}
				onSubmit={handleSubmitAddToMailingList}
			/>

			<ConfirmationDialog
				open={!!selectedMailingDetails}
				title="Delete From Mailing List"
				heading="Are you sure you want to remove this email from mailing list?"
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleDeleteMailingDetails}
				width="sm"
				color="error"
			/>

			<ReportHistoryDialog
				open={showReportHistoryDialog}
				societyId={societyConfig?.id ?? -1}
				onClose={handleCloseReportHistoryDialog}
			/>
		</Box>
	);
};

export default Reporting;
